import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import { decideFold, FOLD_DISCLOSURE_KEY, toggleExpanded, type FoldColumn } from './fold.js';
import { readOpenRows, sessionStore, writeOpenRows } from './fold-memory.js';

// The fold, measured (04-data §1.7 *Folding*; jeeves ADR 0168). The decision is `decideFold`'s, a pure function; this
// hook supplies its two inputs from the laid-out table — the width the table's box has, and each column's least width —
// and holds which rows the reader opened. Why the width of the box and not the viewport: §1.7 decides on the
// container's measured width, never a breakpoint, because a table in a record page's tab has less room than the
// viewport suggests. Why every column is measured with every column shown: a folded column keeps its cells in the
// document, hidden, and the `is-measuring` class lifts the fold for the length of one synchronous read — so the widths
// never depend on the fold of the moment, and the same room always gives the same fold.

export interface ColumnFold {
  /** The keys of the columns folded now. */
  folded: ReadonlySet<string>;
  /**
   * True when the table is wider than its box even with its whole folding set folded. The box then scrolls as a last
   * resort, so no value is clipped out of reach; the conformance spec (test/e2e/conformance/table-fold.spec.ts) holds
   * every table to never needing it.
   */
  overflowing: boolean;
  /** The rows whose folded columns the reader opened, by row key — remembered for the browser session (fold-memory.ts). */
  expanded: ReadonlySet<string>;
  toggle: (rowKey: string) => void;
  tableRef: RefObject<HTMLTableElement | null>;
  scrollerRef: RefObject<HTMLDivElement | null>;
}

interface FoldState {
  folded: readonly string[];
  overflowing: boolean;
}

const NOTHING_FOLDED: FoldState = { folded: [], overflowing: false };

function sameKeys(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((key, index) => key === b[index]);
}

/**
 * Each column's min-content width with every column shown, by its header's `data-column` key — the disclosure column's
 * under FOLD_DISCLOSURE_KEY. Synchronous: the class and the width are put back before the browser paints.
 */
export function measureColumnWidths(table: HTMLTableElement): Map<string, number> {
  const widths = new Map<string, number>();
  const inline = table.style.inlineSize;
  table.classList.add('is-measuring');
  table.style.inlineSize = 'min-content';
  for (const th of table.querySelectorAll<HTMLTableCellElement>(':scope > thead > tr > th[data-column]')) {
    const key = th.dataset.column;
    if (key) widths.set(key, th.getBoundingClientRect().width);
  }
  table.style.inlineSize = inline;
  table.classList.remove('is-measuring');
  return widths;
}

/**
 * Folds a table's declared columns to the width its box measures. `enabled` is false below --bp-md, where the card form
 * (or strategy B's own scrolling box) stands and nothing folds. `content` changes whenever what the cells hold may have:
 * the rows, the columns, the table's state. `memoryKey` names the table for the session's memory of the rows the reader
 * opened (`foldMemoryKey`); omitted, the opened rows are this mount's alone.
 */
export function useColumnFold(enabled: boolean, columns: readonly FoldColumn[], content: { rows: unknown; status: string }, memoryKey?: string): ColumnFold {
  const tableRef = useRef<HTMLTableElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<FoldState>(NOTHING_FOLDED);
  // Why the opened rows start from the session's memory (03-components §13; the owner's ruling Q19, "Remember opened folds"; Phase 1c finding G11): a reader who opened rows, followed one away and came back finds them open again.
  // Why held with the key they were read under: one mounted table can be handed another key — the same page component
  // showing another record — and then its rows are that table's, read afresh during the render that changes it.
  const [stored, setOpen] = useState<{ key: string | undefined; rows: ReadonlySet<string> }>(() => ({
    key: memoryKey,
    rows: memoryKey === undefined ? new Set() : readOpenRows(sessionStore(), memoryKey),
  }));
  let open = stored;
  if (stored.key !== memoryKey) {
    open = { key: memoryKey, rows: memoryKey === undefined ? new Set() : readOpenRows(sessionStore(), memoryKey) };
    setOpen(open);
  }
  const openRef = useRef(open);
  openRef.current = open;
  const stateRef = useRef(state);
  stateRef.current = state;
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const update = useCallback((sync: boolean): void => {
    const apply = (next: FoldState): void => {
      if (sync) flushSync(() => setState(next));
      else setState(next);
    };
    const current = stateRef.current;
    const table = tableRef.current;
    const scroller = scrollerRef.current;
    if (!enabled || !table || !scroller) {
      if (current.folded.length > 0 || current.overflowing) apply(NOTHING_FOLDED);
      return;
    }
    const available = scroller.clientWidth;
    // Why: a table in a hidden tab panel has no width yet; it is measured when its panel is shown (the observer fires).
    if (available <= 0) return;
    const widths = measureColumnWidths(table);
    const decision = decideFold({ available, widths, columns: columnsRef.current, disclosure: widths.get(FOLD_DISCLOSURE_KEY) ?? 0 });
    const shown = sameKeys(decision.folded, current.folded);
    // Why the laid-out width is checked as well, once the decided fold is the one on screen: the decision adds column
    // widths, and a row spanning several columns (a row's detail, a state) could in principle need more than the
    // columns it spans. Only the laid-out table can say so, and then the box scrolls rather than clip.
    const spills = shown && table.getBoundingClientRect().width > available + 1;
    const next: FoldState = { folded: decision.folded, overflowing: !decision.fits || spills };
    if (shown && next.overflowing === current.overflowing) return;
    apply(next);
  }, [enabled]);

  // Why a layout effect: the fold is decided after the DOM is written and before the browser paints, so a table never
  // arrives unfolded and then jumps (ADR 0168, "decided before the rows paint"). It runs again after its own change of
  // state, which is when the laid-out check above can be made.
  useLayoutEffect(() => {
    update(false);
  }, [update, state, columns, content.rows, content.status]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!enabled || !scroller || typeof ResizeObserver === 'undefined') return undefined;
    // Why flushSync: an observer's callback runs after layout and before paint; rendering the new fold inside it keeps
    // a resize from painting one frame of the old fold at the new width. Why the width alone: the fold is the box's width
    // against the columns', and a change of height (a row opened, a page of rows arriving) says nothing about it — what
    // the cells hold reaches the fold through the layout effect above, on every render that changes it.
    let width = -1;
    const observer = new ResizeObserver((entries) => {
      const next = entries[entries.length - 1]?.contentRect.width ?? -1;
      if (next === width) return;
      width = next;
      update(true);
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [enabled, update]);

  const toggle = useCallback((rowKey: string): void => {
    const current = openRef.current;
    const rows = toggleExpanded(current.rows, rowKey);
    if (current.key !== undefined) writeOpenRows(sessionStore(), current.key, rows);
    openRef.current = { key: current.key, rows };
    setOpen(openRef.current);
  }, []);

  return { folded: new Set(state.folded), overflowing: state.overflowing, expanded: open.rows, toggle, tableRef, scrollerRef };
}
